# Privacy Policy and Data Usage for Intuizen

_Last updated: July 28, 2024_

## 1. Introduction

Sovilon Software Inc. ("we," "our," or "us") is committed to protecting your
privacy. This Privacy Policy explains how we collect, use, disclose, and
safeguard your information when you use our Intuizen application ("Intuizen" or
the "Application").

Please read this Privacy Policy carefully. If you do not agree with the terms of
this Privacy Policy, please do not access the Application.

## 2. Collection of Your Information

We collect several types of information from and about users of our Application,
including information:

- By which you may be personally identified such as email address, username, and profile
  image ("personal information");
- About your internet connection, the equipment you use to access our
  Application, and usage details;
- Analytics and application usage insights to improve our services and user
  experience.

## 3. How We Collect Your Information

We collect information from you when you:

- Interact with the Application (registration, for analytics and usage insights)

## 4. How We Use Your Information

We use information that we collect about you or that you provide to us,
including any personal information:

- To provide you with the Application and its contents, and any other
  information, products or services that you request from us;
- To fulfill any other purpose for which you provide it;
- To provide you with notices about your account;
- To notify you about changes to our Application or any products or services we
  offer or provide through it;
- To analyze usage patterns and improve our Application's functionality and user
  experience;
- In any other way we may describe when you provide the information;
- For any other purpose with your consent.

## 5. Disclosure of Your Information

We do not share, sell, or otherwise disclose your personal information for
purposes other than those outlined in this Privacy Policy.

However, we may disclose aggregated information about our users, and information
that does not identify any individual, without restriction.

## 6. Third-Party Services

### 6.1 Voice Transcription

When you choose to use voice transcription features, you may elect to use the "Scribe" which uses the
native voice transcription service provided by your browser (e.g., Apple's
Safari, Google Chrome) or operating system. In these cases, your voice audio
data will be sent to these third-party services for transcription. Please refer
to the privacy policies of these providers for more information on how they
handle your data. The web API is called `SpeechRecognition` and more information can be found here:

https://developer.mozilla.org/en-US/docs/Web/API/Web_Speech_API/Using_the_Web_Speech_API

### 6.2 Text Processing

Once a text transcript is received, it is further processed using large language
model providers, including:

- OpenAI
- Anthropic

Please refer to the privacy policies of these providers for more information on
how they handle this part of the data workflow.

The text of the transcript, and the prompt used for processing, are not stored but may be in the future to improve the application on an ongoing basis.

### 6.2.1 Session Impressions

After the text transcript is processed, artifacts that result from that processing are produced. 
These artifacts can be considered metadata and are stored in a local on-device database.

### 6.3 Image Processing

Images provided as targets are processed using the following third-party
provider:

- OpenAI

Please refer to the privacy and data usage policy of OpenAI for more information
on how they handle your data.

### 6.4 Analytics and Usage Insights

Analytics and usage insights are not collected but may be in the future.

### 6.5 Error Tracking and Performance Monitoring

We use Sentry, a third-party error tracking and performance monitoring service, to help us identify and fix issues in our Application. Sentry may collect information such as:

- Error messages and stack traces
- Device information (e.g., browser type, operating system)
- Performance metrics

This information helps us improve the stability and performance of our Application. Sentry's use of this data is governed by their privacy policy, which can be found at: https://sentry.io/privacy/

## 7. Data Retention

We will only keep your personal information for as long as it is necessary for
the purposes set out in this Privacy Policy, unless a longer retention period is
required or permitted by law.

## 8. Security of Your Information

We use administrative, technical, and physical security measures to help protect
your personal information. While we have taken reasonable steps to secure the
personal information you provide to us, please be aware that despite our
efforts, no security measures are perfect or impenetrable, and no method of data
transmission can be guaranteed against any interception or other type of misuse.

## 9. Your Rights

Depending on where you reside, you may have certain rights with respect to your
personal information, such as the right to request access, correction, or
deletion of your data.

## 10. Changes to Our Privacy Policy

We may update our Privacy Policy from time to time. We will notify you of any
changes by posting the new Privacy Policy on this page and updating the "Last
updated" date at the top of this Privacy Policy.

## 11. Contact Information

To ask questions or comment about this Privacy Policy and our privacy practices,
contact us at mailing address:

Sovilon Software Inc.
Sheridan College - EDGE Entrepreneurship Room B290, Hazel McCallion Campus

℅ Sovilon Software Inc., Michael Nolivos 

4226 Duke of York Blvd
Mississauga, ON L5B 0G5 Canada

or on the sovilon software inc. website.